<template>
  <div ref="rootRef" class="app-select" @keydown="onKeydown">
    <button
      type="button"
      class="app-select-trigger"
      role="combobox"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      :aria-activedescendant="open ? activeId : undefined"
      :disabled="disabled"
      @click="toggle"
    >
      <span>{{ selectedLabel }}</span>
      <ChevronDown :size="16" />
    </button>
    <div v-if="open" :id="listboxId" class="app-select-menu" role="listbox">
      <button
        v-for="(option, index) in options"
        :id="`${listboxId}-${index}`"
        :key="option.value"
        type="button"
        role="option"
        :aria-selected="option.value === modelValue"
        :class="{ active: index === activeIndex, selected: option.value === modelValue }"
        @mouseenter="activeIndex = index"
        @click="choose(option.value)"
      >
        <span class="app-select-option-icon" aria-hidden="true">
          <Check v-if="option.value === modelValue" :size="15" />
        </span>
        <span class="app-select-option-label">{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Check, ChevronDown } from "@lucide/vue";

export type SelectOption = { value: string; label: string };

const props = withDefaults(defineProps<{
  modelValue: string;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}>(), {
  placeholder: "请选择",
  ariaLabel: "选择",
  disabled: false
});
const emit = defineEmits<{ "update:modelValue": [value: string]; change: [value: string] }>();
const rootRef = ref<HTMLElement | null>(null);
const open = ref(false);
const activeIndex = ref(0);
const listboxId = `app-select-${Math.random().toString(36).slice(2, 10)}`;
const selectedLabel = computed(() => props.options.find((option) => option.value === props.modelValue)?.label || props.placeholder);
const activeId = computed(() => `${listboxId}-${activeIndex.value}`);

watch(() => props.modelValue, () => {
  const index = props.options.findIndex((option) => option.value === props.modelValue);
  activeIndex.value = index >= 0 ? index : 0;
});

onMounted(() => document.addEventListener("pointerdown", onDocumentPointerDown));
onUnmounted(() => document.removeEventListener("pointerdown", onDocumentPointerDown));

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    const index = props.options.findIndex((option) => option.value === props.modelValue);
    activeIndex.value = index >= 0 ? index : 0;
  }
}

function choose(value: string) {
  emit("update:modelValue", value);
  emit("change", value);
  open.value = false;
}

function onKeydown(event: KeyboardEvent) {
  if (props.disabled) return;
  if (!open.value && ["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
    event.preventDefault();
    toggle();
    return;
  }
  if (!open.value) return;
  if (event.key === "ArrowDown") {
    event.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % props.options.length;
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + props.options.length) % props.options.length;
  } else if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    const option = props.options[activeIndex.value];
    if (option) choose(option.value);
  } else if (event.key === "Escape") {
    event.preventDefault();
    open.value = false;
  }
}

function onDocumentPointerDown(event: PointerEvent) {
  const target = event.target as Node | null;
  if (target && rootRef.value?.contains(target)) return;
  open.value = false;
}
</script>
